import { Car, Baby, Dog, Briefcase, Shield, Phone, QrCode, Package, MapPin } from "lucide-react";
import { IMAGES } from "./constants";
import { useI18n } from "../../i18n";

const DesignTabs = ({ activeTab, setActiveTab }) => {
  const { t } = useI18n();
  const tabs = [
    { key: "vehiculos", icon: Car },
    { key: "ninos", icon: Baby },
    { key: "mascotas", icon: Dog },
    { key: "objetos", icon: Briefcase },
  ];

  const d = (k) => t(`designs.${activeTab}.${k}`);

  return (
    <section id="disenos" className="bg-white py-20 md:py-28" data-testid="design-tabs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-[#0B2545] sm:text-4xl lg:text-5xl">
            {t("designs.title")}
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            {t("designs.subtitle")}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {tabs.map(({ key, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`design-tab-${key}`}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                activeTab === key
                  ? "bg-[#1B84FF] text-white shadow-lg shadow-[#1B84FF]/30 scale-105"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t(`designs.tabs.${key}`)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="relative min-h-[400px]">
          {activeTab === "vehiculos" && (
            <div className="animate-in fade-in duration-300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-w-4xl mx-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <img
                  src={IMAGES.carRear1}
                  alt={d("badge")}
                  className="w-full h-[300px] md:h-[400px] object-cover object-center transition-transform duration-300 hover:scale-[1.02]"
                  loading="eager"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent flex items-end p-6 md:p-10">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-[#1B84FF] text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                      <Car className="w-4 h-4" />
                      {d("badge")}
                    </div>
                    <h3 className="text-white font-bold text-2xl md:text-3xl mb-2">{d("hero_title")}</h3>
                    <p className="text-white/90 text-sm md:text-base max-w-lg">{d("hero_text")}</p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="relative rounded-2xl overflow-hidden shadow-lg group hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                  <img
                    src={IMAGES.carRear2}
                    alt={d("card1_title")}
                    className="w-full h-56 object-cover object-center transition-transform duration-300 group-hover:scale-[1.02]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-5">
                    <div>
                      <span className="text-white font-semibold text-lg">{d("card1_title")}</span>
                      <p className="text-white/80 text-sm">{d("card1_text")}</p>
                    </div>
                  </div>
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#0B2545] flex items-center justify-center h-56">
                  <div className="text-center text-white p-6">
                    <QrCode className="w-12 h-12 mx-auto mb-3 opacity-80" />
                    <h4 className="text-xl font-bold mb-2">{d("card2_title")}</h4>
                    <ul className="text-white/80 text-sm space-y-1">
                      {(d("card2_bullets") || []).map((b, i) => (
                        <li key={i}>• {b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "ninos" && (
            <div className="animate-in fade-in duration-300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-w-4xl mx-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <img
                  src={IMAGES.kids}
                  alt={d("badge")}
                  className="w-full h-[300px] md:h-[400px] object-cover object-top transition-transform duration-300 hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent flex items-end p-6 md:p-10">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-[#0B2545] text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                      <Baby className="w-4 h-4" />
                      {d("badge")}
                    </div>
                    <h3 className="text-white font-bold text-2xl md:text-3xl mb-2 drop-shadow-lg">{d("hero_title")}</h3>
                    <p className="text-white/90 text-sm md:text-base max-w-lg drop-shadow-md">{d("hero_text")}</p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-[#1B84FF] text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                  {d("best_seller")}
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Baby className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card1_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card1_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Shield className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card2_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card2_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Phone className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card3_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card3_text")}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "mascotas" && (
            <div className="animate-in fade-in duration-300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-w-4xl mx-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <img
                  src={IMAGES.dogs}
                  alt={d("badge")}
                  className="w-full h-[300px] md:h-[400px] object-cover object-center transition-transform duration-300 hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent flex items-end p-6 md:p-10">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-[#0B2545] text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                      <Dog className="w-4 h-4" />
                      {d("badge")}
                    </div>
                    <h3 className="text-white font-bold text-2xl md:text-3xl mb-2 drop-shadow-lg">{d("hero_title")}</h3>
                    <p className="text-white/90 text-sm md:text-base max-w-lg drop-shadow-md">{d("hero_text")}</p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Dog className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card1_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card1_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Shield className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card2_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card2_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <MapPin className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card3_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card3_text")}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "objetos" && (
            <div className="animate-in fade-in duration-300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8 max-w-4xl mx-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <img
                  src={IMAGES.objects}
                  alt={d("badge")}
                  className="w-full h-[300px] md:h-[400px] object-cover object-center transition-transform duration-300 hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent flex items-end p-6 md:p-10">
                  <div>
                    <div className="inline-flex items-center gap-2 bg-[#0B2545] text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                      <Briefcase className="w-4 h-4" />
                      {d("badge")}
                    </div>
                    <h3 className="text-white font-bold text-2xl md:text-3xl mb-2 drop-shadow-lg">{d("hero_title")}</h3>
                    <p className="text-white/90 text-sm md:text-base max-w-lg drop-shadow-md">{d("hero_text")}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {(d("chips") || []).map((chip, i) => (
                        <span key={i} className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">{chip}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Package className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card1_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card1_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <QrCode className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card2_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card2_text")}</p>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-center shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-transform duration-300 hover:-translate-y-1">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EAF4FF]">
                    <Phone className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <h4 className="mb-2 font-bold text-[#0B2545]">{d("card3_title")}</h4>
                  <p className="text-sm text-slate-600">{d("card3_text")}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DesignTabs;
